import React, { useEffect, useMemo, useState } from "react";
import RowList from "../../../components/common/rowList";
import DeviceLogChart from "../../../components/devices/deviceLogChart";
import useDarkMode from "../../../hooks/useDarkMode";

const INLET_METRIC_CONFIGS = {
  currentRms: {
    metricKey: "currentRms",
    metricLabel: "Current RMS",
    optionLabel: "Current RMS",
    title: "Inlet Current RMS",
    unit: "A",
    color: "#2563eb",
  },
  voltageRms: {
    metricKey: "voltageRms",
    metricLabel: "Voltage RMS",
    optionLabel: "Voltage RMS",
    title: "Inlet Voltage RMS",
    unit: "V",
    color: "#f97316",
  },
};

const INLET_SUMMARY_METRICS = [
  { key: "inletCurrentRms",          label: "Current RMS",        unit: "A",   decimals: 2, color: "#2563eb" },
  { key: "inletActivePower",         label: "Active Power",       unit: "W",   decimals: 2, color: "#10b981" },
  { key: "inletEnergyAccumulation",  label: "Energy Consumption", unit: "kWh", decimals: 2, color: "#f59e0b" },
];

/**
 * DevicePageInletEnhanced – single‑inlet drill‑down view (inline‑style version)
 * ---------------------------------------------------------------------------
 * •  Headline StatCards highlight Current RMS, Active Power, kWh.
 * •  Works gracefully when only one metric exists (RMS‑only PDUs).
 * •  All visual tweaks use inline `style` props—no external CSS required.
 */
export default function DevicePageInletEnhanced({ inlets = [], banks = [], loading, device }) {
  const inlet = inlets[0] || {}; // focus on first inlet (typical case)
  const serialNumber = device?.enclosureSerialNumber || inlet.enclosureSerialNumber;
  const inletIdentifier = (() => {
    if (!inlet?.id) return "";
    const baseName = inlet.inletName || `Inlet ${inlet.id}`;
    const nameWithId = inlet.inletName ? `${baseName} (${inlet.id})` : baseName;
    if (device?.deviceName) {
      return `${device.deviceName} • ${nameWithId}`;
    }
    return nameWithId;
  })();
  const inletEnergyCapable = device?.inletEnergySupported === true;
  const inletCurrentFlag = device?.inletCurrentSupported;

  const supportsCurrentLogs = Boolean(
    serialNumber &&
    inlet?.id &&
    (inletEnergyCapable || inletCurrentFlag !== false)
  );

  const supportsVoltageLogs = Boolean(
    serialNumber &&
    inlet?.id &&
    inletEnergyCapable
  );

  const availableMetricKeys = useMemo(() => {
    const keys = [];
    if (supportsCurrentLogs) keys.push("currentRms");
    if (supportsVoltageLogs) keys.push("voltageRms");
    return keys;
  }, [supportsCurrentLogs, supportsVoltageLogs]);

  const [selectedMetricKey, setSelectedMetricKey] = useState(() => availableMetricKeys[0] || "currentRms");

  useEffect(() => {
    const fallbackKey = availableMetricKeys[0] || "currentRms";
    if (!availableMetricKeys.includes(selectedMetricKey) && selectedMetricKey !== fallbackKey) {
      setSelectedMetricKey(fallbackKey);
    }
  }, [availableMetricKeys, selectedMetricKey]);

  useEffect(() => {
    if (availableMetricKeys.length === 0) return;
    const defaultKey = availableMetricKeys[0] || "currentRms";
    setSelectedMetricKey((current) => (current === defaultKey ? current : defaultKey));
  }, [inlet?.id, availableMetricKeys]);

  const activeMetricKey = availableMetricKeys.includes(selectedMetricKey)
    ? selectedMetricKey
    : availableMetricKeys[0];

  const activeMetric = INLET_METRIC_CONFIGS[activeMetricKey] || INLET_METRIC_CONFIGS.currentRms;

  const showMetricSelector =
    inletEnergyCapable &&
    availableMetricKeys.includes("currentRms") &&
    availableMetricKeys.includes("voltageRms");

  const metricControls =
    showMetricSelector ? (
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.35rem",
          fontSize: "0.85rem",
          color: "#374151",
        }}
      >
        <span style={{ fontWeight: 500 }}>Metric</span>
        <select
          value={activeMetricKey}
          onChange={(event) => setSelectedMetricKey(event.target.value)}
          style={{
            margin: 0,
            minWidth: "140px",
            padding: "0.35rem 0.5rem",
            borderRadius: "4px",
            border: "1px solid #d7dde2",
            backgroundColor: "#fff",
          }}
        >
          {availableMetricKeys.map((key) => {
            const config = INLET_METRIC_CONFIGS[key];
            return (
              <option key={key} value={key}>
                {config?.optionLabel || key}
              </option>
            );
          })}
        </select>
      </label>
    ) : null;

  /* ── headline metric config ─────────────────────────────────────────── */
  const headlineMetrics = INLET_SUMMARY_METRICS
    .map((metric) => {
      const rawValue = inlet?.[metric.key];
      if (rawValue === undefined || rawValue === null) return null;

      const numericValue = Number(rawValue);
      const formattedValue = Number.isFinite(numericValue)
        ? numericValue.toFixed(metric.decimals)
        : String(rawValue);

      return { ...metric, value: formattedValue };
    })
    .filter(Boolean);

  const dark = useDarkMode();

  if (loading) return <InletSkeleton />;

  /* ── render ─────────────────────────────────────────────────────────── */
  return (
    <div className="grid-container fluid">
      {/* Headline summary tiles */}
      {headlineMetrics.length > 0 && (
        <div style={SUMMARY_GRID_STYLE} className="device-inlet-summary-grid">
          {headlineMetrics.map(({ key, label, value, unit, color }) => (
            <SummaryTile key={key} label={label} value={value} unit={unit} color={color} dark={dark} />
          ))}
        </div>
      )}

      {/* Detail lists */}
      <div className="grid-x grid-padding-y grid-padding-x">
        {/* Inlet stats */}
        <div className="cell large-6">
          <RowList
            rows={[
              { label: "Inlet Stats", labelStyle: { fontWeight: "bold", fontSize: "110%" } },
              { label: "Inlet ID",              value: inlet.id },
              { label: "Current RMS",           value: <DataValue value={inlet.inletCurrentRms} unit="A" /> },
              { label: "Voltage RMS",           value: <DataValue value={inlet.inletVoltageRms} unit="V" /> },
              { label: "Line Frequency",        value: <DataValue value={inlet.inletLineFrequency} unit="Hz" /> },
              { label: "Power Factor",          value: <DataValue value={inlet.inletPowerFactor} unit="" /> },
              { label: "Energy Consumption",    value: <DataValue value={inlet.inletEnergyAccumulation} unit="kWh" /> },
              { label: "Active Power",          value: <DataValue value={inlet.inletActivePower} unit="W" /> },
              { label: "Apparent Power",        value: <DataValue value={inlet.inletApparentPower} unit="VA" /> },
            ]}
          />
        </div>

        {/* Meta / static info */}
        <div className="cell large-6">
          <RowList
            rows={[
              { label: "Inlet Information", labelStyle: { fontWeight: "bold", fontSize: "110%" } },
              { label: "Enclosure Serial Number", value: inlet.enclosureSerialNumber },
              { label: "Inlet Plug",               value: inlet.inletPlug },
              { label: "Inlet Type",               value: inlet.inletType === "dual" ? "Dual Circuit" : inlet.inletType === "ats" ? "ATS" : "Standard" },
              { label: "Inlet Phase",              value: inlet.inletPhase },
              { label: "Connection State",         value: inlet.connectionState },
            ]}
          />
        </div>
      </div>
      {serialNumber && inlet?.id && availableMetricKeys.length > 0 && activeMetric && (
        <div className="grid-x grid-padding-x grid-padding-y">
          <div className="cell large-12">
            <DeviceLogChart
              title={activeMetric.title}
              subtitle={inletIdentifier}
              logType="inlets"
              enclosureSerialNumber={serialNumber}
              resourceId={inlet.id}
              metricKey={activeMetric.metricKey}
              metricLabel={activeMetric.metricLabel}
              unit={activeMetric.unit}
              color={activeMetric.color}
              controls={metricControls}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const InletSkeleton = () => {
  const dark = useDarkMode();

  const skeletonLine = (
    <div
      className="skeleton-blank"
      style={{ width: "120px", height: "14px", margin: 0, padding: 0 }}
    />
  );

  return (
    <div className="grid-container fluid">
      <div style={SUMMARY_GRID_STYLE} className="device-inlet-summary-grid">
        {INLET_SUMMARY_METRICS.map((metric) => (
          <div key={metric.key} className="skeleton-blank" style={SUMMARY_SKELETON_TILE_STYLE.tile(dark)}>
            <div style={{ ...SUMMARY_TILE_STYLE.accent, background: metric.color, width: "100%" }} />
          </div>
        ))}
      </div>
      <div className="grid-x grid-padding-y grid-padding-x">
        <div className="cell large-6">
          <RowList
            rows={[
              { label: "Inlet Stats", labelStyle: { fontWeight: "bold", fontSize: "110%" } },
              { label: "Inlet ID", value: skeletonLine },
              { label: "Current RMS", value: skeletonLine },
              { label: "Voltage RMS", value: skeletonLine },
              { label: "Line Frequency", value: skeletonLine },
              { label: "Power Factor", value: skeletonLine },
              { label: "Energy Consumption", value: skeletonLine },
              { label: "Active Power", value: skeletonLine },
              { label: "Apparent Power", value: skeletonLine },
            ]}
          />
        </div>
        <div className="cell large-6">
          <RowList
            rows={[
              { label: "Inlet Information", labelStyle: { fontWeight: "bold", fontSize: "110%" } },
              { label: "Enclosure Serial Number", value: skeletonLine },
              { label: "Inlet Plug", value: skeletonLine },
              { label: "Inlet Type", value: skeletonLine },
              { label: "Inlet Phase", value: skeletonLine },
              { label: "Connection State", value: skeletonLine },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

/* ── helpers ───────────────────────────────────────────────────────────── */
const SummaryTile = ({ label, value, unit, color, dark }) => {
  return (
    <div style={SUMMARY_TILE_STYLE.wrap(dark)} className="devices-summary-tile">
      <div style={SUMMARY_TILE_STYLE.big(dark)}>
        <span>{value}</span>
        {unit ? <span style={SUMMARY_TILE_STYLE.unit(dark)}>{unit}</span> : null}
      </div>
      <div style={SUMMARY_TILE_STYLE.label(dark)}>{label}</div>
      <div style={{ ...SUMMARY_TILE_STYLE.accent, background: color, width: "100%" }} />
    </div>
  );
};

const DataValue = ({ value, unit }) => {
  return (
    <span style={{ display: "flex", alignItems: "flex-end" }}>
      <span
        style={{
          fontWeight: "600",
          fontSize: "105%",
        }}
      >
        {(value === undefined) ? (
          "-"
        ) : (
          (value || 0).toFixed(2)
        )}
      </span>
      <span
        style={{
          fontSize: "80%",
          paddingLeft: "4px",
          paddingBottom: "2px",
        }}
      >
        {unit}
      </span>
    </span>
  );
};

const SUMMARY_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  gap: 16,
  marginBottom: "24px",
  marginTop: "20px"
};

const SUMMARY_TILE_STYLE = {
  wrap: (dark) => ({
    background: dark ? "#424242" : "#fff",
    borderRadius: 3,
    padding: "20px 16px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    position: "relative",
    minHeight: 92,
  }),
  big: (dark) => ({
    fontSize: 28,
    fontWeight: 600,
    lineHeight: 1,
    color: dark ? "#f3f3f3" : "#2c3e50",
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  }),
  unit: (dark) => ({
    fontSize: 14,
    color: dark ? "#d1d5db" : "#6b7280",
    fontWeight: 500,
  }),
  label: (dark) => ({
    fontSize: 13,
    marginTop: 6,
    color: dark ? "#ccc" : "#7f8c8d",
  }),
  accent: {
    position: "absolute",
    left: 0,
    bottom: 0,
    height: 4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    transition: "width .4s ease",
  },
};

const SUMMARY_SKELETON_TILE_STYLE = {
  tile: (dark) => ({
    ...SUMMARY_TILE_STYLE.wrap(dark),
    background: dark ? "#555" : "rgb(230,230,230)",
    marginTop: 0,
    marginBottom: 0,
    padding: 0,
    boxShadow: "none",
  }),
};
